import React from 'react';
import { useTimezone } from '@app/contexts/useTimezone';
import { BaselineDropdown } from '../common/BaselineDropdown';

interface BaselineComparisonControlsProps {
  baselineLabel: string;
  availableBaselines: string[];
  aggregationMode: 'hourly' | 'daily';
  alignmentMode?: 'native' | 'common';
  onBaselineChange: (baseline: string) => void;
  onAggregationModeChange: (mode: 'hourly' | 'daily') => void;
  onAlignmentModeChange?: (mode: 'native' | 'common') => void;
}

const BaselineComparisonControls: React.FC<BaselineComparisonControlsProps> = ({
  baselineLabel,
  availableBaselines,
  aggregationMode,
  alignmentMode = 'native',
  onBaselineChange,
  onAggregationModeChange,
  onAlignmentModeChange,
}) => {
  useTimezone();
  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
      {/* Aggregation Toggle - moved to left */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
          Aggregation
        </label>
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md">
          <button
            onClick={() => onAggregationModeChange('hourly')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
              aggregationMode === 'hourly'
                ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            Hourly
          </button>
          <button
            onClick={() => onAggregationModeChange('daily')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
              aggregationMode === 'daily'
                ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            Daily
          </button>
        </div>
      </div>

      {/* Alignment Toggle (hourly only) */}
      {aggregationMode === 'hourly' && (
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
            Alignment
          </label>
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md">
            <button
              onClick={() => onAlignmentModeChange && onAlignmentModeChange('native')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                alignmentMode === 'native'
                  ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Native
            </button>
            <button
              onClick={() => onAlignmentModeChange && onAlignmentModeChange('common')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                alignmentMode === 'common'
                  ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              Common only
            </button>
          </div>
        </div>
      )}

      {/* Baseline Selector - moved to right */}
      <BaselineDropdown
        availableBaselines={availableBaselines}
        selectedBaseline={baselineLabel}
        onBaselineChange={onBaselineChange}
        displayStyle="dropdown"
      />
    </div>
  );
};

export default BaselineComparisonControls;
